import { useMemo } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useUpdateNetworkNotificationSettings,
} from '@tribeplatform/react-sdk/hooks'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SettingsLayout } from '../../Layout/SettingsLayout.js'

export const EmailNotificationSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNotificationSettings, isLoading } =
    useUpdateNetworkNotificationSettings()

  const emailSettings = useMemo(() => {
    if (!network?.settings?.emailEnabled) {
      return 'none'
    }
    if (!network?.settings?.emailNotificationEnabled) {
      return 'essential'
    }
    return 'all'
  }, [
    network?.settings?.emailEnabled,
    network?.settings?.emailNotificationEnabled,
  ])

  const onSubmit = async ({ emailSettings }) => {
    try {
      const input = {
        emailEnabled: emailSettings !== 'none',
        emailNotificationEnabled: emailSettings === 'all',
      }
      await updateNotificationSettings({
        input,
      })
      toast({
        title: $t({
          defaultMessage: 'Email notification settings updated.',
          id: 'Admin.NotificationSettings.Email.Updated',
        }),
        status: 'success',
      })
    } catch (err) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update email notification settings.',
          id: 'Admin.NotificationSettings.Email.FailedToUpdate',
        }),
        status: 'error',
      })
      logger.error('Unabled to update email notification settings', err)
    }
  }

  return (
    <Form
      defaultValues={{
        emailSettings,
      }}
      onSubmit={onSubmit}
    >
      {({ formState: { isDirty }, reset }) => (
        <SettingsLayout
          title={$t({
            defaultMessage: 'Email notification settings',
            id: 'Admin.NotificationSettings.Email',
          })}
          subtitle={$t({
            defaultMessage:
              'As an admin, you can control what type of emails would be sent to {MEMBERS}. If you are planning to handle email notifications on your side, then you might want to disable outgoing emails from the community.',
            id: 'Admin.NotificationSettings.Email.Description',
          })}
          disabled={!isDirty}
          isLoading={isLoading}
          onCancel={() => reset()}
          saveLabel={<T defaultMessage="Update" id="Generics.Update" />}
        >
          <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
            <Form.RadioGroup name="emailSettings">
              <RadioGroup.Items>
                <RadioGroup.Item
                  value="all"
                  title={$t({
                    defaultMessage: 'All notification emails',
                    id: 'Admin.NotificationSettings.Email.All',
                  })}
                  description={$t({
                    defaultMessage:
                      '{MEMBERS_CC} would receive all notifications including community-wide and essential emails',
                    id: 'Admin.NotificationSettings.Email.All.Description',
                  })}
                />
                <RadioGroup.Item
                  value="essential"
                  title={$t({
                    defaultMessage: 'Only essential notifications',
                    id: 'Admin.NotificationSettings.Email.Essentials',
                  })}
                  description={$t({
                    defaultMessage:
                      '{MEMBERS_CC} will only receive essential emails such as invitations and password-reset and email digest (if enabled).',
                    id: 'Admin.NotificationSettings.Email.Essentials.Description',
                  })}
                />
                <RadioGroup.Item
                  value="none"
                  title={$t({
                    defaultMessage: 'Turn off all notifications',
                    id: 'Admin.NotificationSettings.Email.None',
                  })}
                  description={$t({
                    defaultMessage:
                      'No email from the community would be sent on behalf of the community',
                    id: 'Admin.NotificationSettings.Email.None.Description',
                  })}
                />
              </RadioGroup.Items>
            </Form.RadioGroup>
          </div>
        </SettingsLayout>
      )}
    </Form>
  )
}
